import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconClipboard from '@apollo/icons/default/IconClipboard.svg';
import IconDownload from '@apollo/icons/default/IconDownload.svg';
import IconFilter from '@apollo/icons/default/IconFilter.svg';
import IconKeyboard from '@apollo/icons/default/IconKeyboard.svg';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Switch,
} from '@apollo/orbit';
import copy from 'copy-to-clipboard';
import { saveAs } from 'file-saver';
import { getIntrospectionQuery, graphqlSync } from 'graphql';
import React from 'react';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { toggleShortcutModal } from 'src/hooks/useKeypressListener/hotkeyDictionary';
import { hotkeys } from 'src/hooks/useKeypressListener/useKeypressListener';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { buildSchemaLeniently } from 'src/lib/buildSchemaLeniently';

import { SchemaInfo } from '../sdlDisplay/SDLDisplay';

export const downloadRawSdl = (
  sdl: string,
  graphRef: GraphRef | null,
  subgraphName: string | null | undefined,
) => {
  saveAs(
    new Blob([sdl], {
      type: 'text/plain;charset=utf-8',
    }),
    graphRef
      ? subgraphName
        ? `${graphRef.graphId}@${graphRef.graphVariant}--${subgraphName}.graphql`
        : `${graphRef.graphId}@${graphRef.graphVariant}.graphql`
      : 'schema.graphql',
  );
};

export const downloadJSONSdl = (
  sdl: string,
  graphRef: GraphRef | null,
  subgraphName: string | null | undefined,
) => {
  const schema = buildSchemaLeniently(sdl).schema;
  saveAs(
    new Blob(
      [
        JSON.stringify(
          graphqlSync(schema, getIntrospectionQuery({})).data,
          null,
          2,
        ),
      ],
      {
        type: 'application/json',
      },
    ),
    graphRef
      ? subgraphName
        ? `${graphRef.graphId}@${graphRef.graphVariant}--${subgraphName}.json`
        : `${graphRef.graphId}@${graphRef.graphVariant}.json`
      : 'schema.json',
  );
};

export const EditorActions = ({
  selectedSchemaInfo,
  setShowComments,
  showComments,
  setShowDeprecated,
  showDeprecated,
  setKeyboardModalVisible,
  graphRef,
  hasSubgraphs,
  selectedSchemaName,
}: {
  selectedSchemaInfo: SchemaInfo | undefined;
  setShowComments: React.Dispatch<React.SetStateAction<boolean>>;
  showComments: boolean;
  setShowDeprecated: React.Dispatch<React.SetStateAction<boolean>>;
  showDeprecated: boolean;
  setKeyboardModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  graphRef: GraphRef | null;
  hasSubgraphs: boolean;
  selectedSchemaName: string | undefined | null;
}) => {
  const [filtersHidden, setFiltersHidden] = React.useState(true);
  const [sdlDownloadFormat, setSdlDownloadFormat] =
    useLocalStorage('sdlDownloadFormat');

  if (!selectedSchemaInfo) return null;

  function downloadSdl(format: 'raw' | 'json') {
    if (!selectedSchemaInfo) {
      return;
    }

    const download = format === 'raw' ? downloadRawSdl : downloadJSONSdl;

    download(selectedSchemaInfo.text, graphRef, selectedSchemaName);
  }

  return (
    <>
      <div className="absolute right-0 top-0 z-10 m-4 flex items-center pr-4">
        <ButtonGroup>
          <CopySDLButton sdl={selectedSchemaInfo.text} />
          <ButtonGroup isAttached>
            {sdlDownloadFormat === 'raw' ? (
              <DownloadSdlButton onClick={() => downloadSdl('raw')} />
            ) : (
              <DownloadJSONButton onClick={() => downloadSdl('json')} />
            )}
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="More options"
                size="sm"
                variant="secondary"
                icon={<IconChevronDown />}
              />
              <MenuContent>
                <MenuList>
                  <MenuOptionGroup
                    type="radio"
                    value={sdlDownloadFormat}
                    onChange={(format) => {
                      setSdlDownloadFormat(format);
                      downloadSdl(format);
                    }}
                  >
                    <MenuItemOption type="radio" value="raw">
                      Raw
                    </MenuItemOption>
                    <MenuItemOption type="radio" value="json">
                      JSON
                    </MenuItemOption>
                  </MenuOptionGroup>
                </MenuList>
              </MenuContent>
            </Menu>
          </ButtonGroup>
        </ButtonGroup>
      </div>
      <div className="absolute bottom-0 right-0 z-10 m-6 mr-16 flex">
        {!hasSubgraphs && (
          <KeyboardShortcutCTA
            setKeyboardModalVisible={setKeyboardModalVisible}
          />
        )}
      </div>
      <div className="absolute bottom-0 right-0 z-20 m-6 flex">
        {filtersHidden ? (
          <Tooltip label="Filter subgraph view">
            <IconButton
              aria-label="Filter subgraph view"
              type="button"
              size="sm"
              variant="hidden"
              icon={<IconFilter />}
              onClick={() => setFiltersHidden(false)}
            />
          </Tooltip>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>
                <IconFilter className="text-icon-primary" />
                View options
              </CardTitle>
              <IconButton
                aria-label="Hide filters"
                type="button"
                size="sm"
                variant="hidden"
                icon={<IconChevronDown />}
                onClick={() => setFiltersHidden(true)}
              />
            </CardHeader>
            <CardBody>
              <Switch
                isChecked={showComments}
                onChange={(e) => setShowComments(e.target.checked)}
              >
                Show comments
              </Switch>
              <Switch
                isChecked={showDeprecated}
                onChange={(e) => setShowDeprecated(e.target.checked)}
              >
                Show deprecated items
              </Switch>
            </CardBody>
          </Card>
        )}
      </div>
    </>
  );
};

const DownloadSdlButton = ({ onClick }: { onClick: () => void }) => (
  <Tooltip label="Download the raw SDL file">
    <Button
      size="sm"
      variant="secondary"
      type="button"
      onClick={onClick}
      leftIcon={<IconDownload className="text-icon-primary" />}
    >
      Raw
    </Button>
  </Tooltip>
);

const DownloadJSONButton = ({ onClick }: { onClick: () => void }) => (
  <Tooltip label="Download the Introspection JSON">
    <Button
      size="sm"
      variant="secondary"
      type="button"
      onClick={onClick}
      leftIcon={<IconDownload className="text-icon-primary" />}
    >
      JSON
    </Button>
  </Tooltip>
);

const CopySDLButton = ({ sdl }: { sdl: string }) => (
  <Tooltip label="Copy the entire SDL file" clickedLabel="Copied">
    <Button
      size="sm"
      variant="secondary"
      type="button"
      onClick={() => {
        copy(sdl);
      }}
      leftIcon={<IconClipboard />}
    >
      Copy
    </Button>
  </Tooltip>
);

export const KeyboardShortcutCTA = ({
  setKeyboardModalVisible,
}: {
  setKeyboardModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}) => (
  <div className="flex justify-between">
    <div className="text-sm text-secondary">
      {hotkeys.peekPreview.label} for peek preview
    </div>
    <div className="rounded text-xs">
      <Tooltip label={`Show keyboard shortcuts (${toggleShortcutModal.label})`}>
        <IconButton
          aria-label={`Show keyboard shortcuts (${toggleShortcutModal.label})`}
          className="ml-4"
          type="button"
          size="sm"
          variant="hidden"
          icon={<IconKeyboard />}
          onClick={() => setKeyboardModalVisible(true)}
        />
      </Tooltip>
    </div>
  </div>
);
